import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { analyticsAPI } from "../../utils/analyticsAPI";
import MetricCard from "../../components/MetricCard";
import { showToast } from "../../components/Toast";
import { FiUsers, FiUserCheck, FiFileText, FiBriefcase } from "react-icons/fi";
import "../../components/Loaders.css";
import DonutChart from "../../components/DonutChart";
import { useTranslation } from "react-i18next";

function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    clients: 0,
    analysts: 0,
    admins: 0,
    reports: 0,
    organizations: 0,
  });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const summary = await analyticsAPI.getSummary();
      setStats({
        clients: summary?.users?.clients || 0,
        analysts: summary?.users?.analysts || 0,
        admins: summary?.users?.admins || 0,
        reports: summary?.reports || 0,
        organizations: summary?.organizations || 0,
      });
    } catch (err) {
      showToast.error(
        err.message ||
          t("admin.loadStatsFailed", "Failed to load dashboard stats"),
      );
    } finally {
      setLoading(false);
    }
  };

  const StatValue = ({ value }) => (
    <div className="stat-value">
      {loading ? (
        <div
          className="skeleton-line"
          style={{ width: "64px", height: "20px" }}
        />
      ) : (
        value
      )}
    </div>
  );

  const ChartSkeleton = () => (
    <div className="skeleton-card" style={{ height: "260px" }}>
      <div className="skeleton-line" style={{ width: "40%" }} />
      <div
        className="skeleton-line"
        style={{ width: "140px", height: "140px", borderRadius: "50%" }}
      />
      <div className="skeleton-line" />
      <div className="skeleton-line" style={{ width: "70%" }} />
    </div>
  );

  const userPieData = {
    labels: ["Clients", "Analysts", "Admins"],
    datasets: [
      {
        data: [stats.clients, stats.analysts, stats.admins],
        backgroundColor: [
          "rgba(59, 130, 246, 0.85)",
          "rgba(34, 197, 94, 0.85)",
          "rgba(249, 115, 22, 0.85)",
        ],
        borderColor: [
          "rgb(59, 130, 246)",
          "rgb(34, 197, 94)",
          "rgb(249, 115, 22)",
        ],
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const orgReportPieData = {
    labels: ["Organizations", "Reports"],
    datasets: [
      {
        data: [stats.organizations, stats.reports],
        backgroundColor: [
          "rgba(99, 102, 241, 0.85)",
          "rgba(239, 68, 68, 0.85)",
        ],
        borderColor: ["rgb(99, 102, 241)", "rgb(239, 68, 68)"],
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  return (
    <div className="page-content">
      <div className="greeting-section" style={{ marginBottom: "32px" }}>
        <h1>
          {t("dashboard.welcomeBack", "Welcome back")},{" "}
          {user?.first_name || user?.name || t("admin.fallbackName", "Admin")}!
        </h1>
        <p>
          {t(
            "admin.overviewIntro",
            "Here's your platform overview at a glance.",
          )}
        </p>
      </div>

      <div className="stats-grid">
        <MetricCard
          label={t("admin.totalClients", "Total Clients")}
          value={<StatValue value={stats.clients} />}
          tone="primary"
          size="lg"
          icon={<FiUsers />}
          onClick={() => navigate("/admin/users")}
        />
        <MetricCard
          label={t("admin.totalAnalysts", "Total Analysts")}
          value={<StatValue value={stats.analysts} />}
          tone="success"
          icon={<FiUserCheck />}
          onClick={() => navigate("/admin/register-user")}
        />
        <MetricCard
          label={t("admin.totalReports", "Total Reports")}
          value={<StatValue value={stats.reports} />}
          tone="warning"
          icon={<FiFileText />}
          onClick={() => navigate("/admin/view-reports")}
        />
        <MetricCard
          label={t("admin.activeOrganizations", "Active Organizations")}
          value={<StatValue value={stats.organizations} />}
          tone="default"
          icon={<FiBriefcase />}
          onClick={() => navigate("/admin/organizations")}
        />
      </div>
      <div className="card" style={{ marginTop: "24px" }}>
        <h2 className="card-title">
          {t("admin.overviewCharts", "Overview Charts")}
        </h2>
        <p style={{ color: "var(--text-grey)", marginTop: "6px" }}>
          {t(
            "admin.overviewChartsSubtitle",
            "Visual breakdown of users and organizational activity.",
          )}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
            marginTop: "16px",
          }}
        >
          {loading ? (
            <>
              <ChartSkeleton />
              <ChartSkeleton />
            </>
          ) : (
            <>
              <div className="card" style={{ height: "320px" }}>
                <h3 style={{ marginTop: 0 }}>
                  {t("admin.usersByRole", "Users by Role")}
                </h3>
                <DonutChart data={userPieData} height="240px" />
              </div>
              <div className="card" style={{ height: "320px" }}>
                <h3 style={{ marginTop: 0 }}>
                  {t(
                    "admin.organizationsVsReports",
                    "Organizations vs Reports",
                  )}
                </h3>
                <DonutChart data={orgReportPieData} height="240px" />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="card">
        <h2 className="card-title">
          {t("admin.dashboardTitle", "Admin Dashboard")}
        </h2>
        <p style={{ color: "var(--text-grey)", lineHeight: "1.6" }}>
          {t("admin.welcomeText", "Welcome to the DSTL administration portal.")}
        </p>
      </div>
    </div>
  );
}
export default AdminDashboard;
